import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  User,
  Calendar,
  MapPin,
  ArrowRight,
  TrendingUp,
  LayoutGrid,
  LayoutList,
  Target as TargetIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  corrective: 'Corrective',
  preventive: 'Preventive',
  improvement: 'Improvement',
  compliance: 'Compliance',
  investigation: 'Investigation',
};

const categoryColors = {
  corrective: 'bg-red-100 text-red-700 border-red-300',
  preventive: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  improvement: 'bg-sky-100 text-sky-700 border-sky-300',
  compliance: 'bg-purple-100 text-purple-700 border-purple-300',
  investigation: 'bg-amber-100 text-amber-700 border-amber-300',
};

const priorityColors = {
  critical: 'bg-red-100 text-red-700 border-red-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-300',
  low: 'bg-slate-100 text-slate-600 border-slate-300',
};

const statusColors = {
  open: 'bg-sky-100 text-sky-700 border-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  blocked: 'bg-red-100 text-red-700 border-red-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  verified: 'bg-purple-100 text-purple-700 border-purple-300',
  closed: 'bg-slate-100 text-slate-600 border-slate-300',
};

const sourceTypeLabels = {
  site_visit: 'Site Visit',
  maintenance: 'Maintenance',
  outage: 'Outage',
  monitoring: 'Monitoring',
  audit: 'Audit',
  manual: 'Manual',
};

export default function ActionPlansPage() {
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sites, setSites] = useState([]);

  const [newAction, setNewAction] = useState({
    title: '',
    description: '',
    site: '',
    category: 'corrective',
    priority: 'medium',
    risk_level: 'medium',
    owner: '',
    due_date: '',
    sla_hours: 72,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [actionsRes, statsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/action-plans`),
        axios.get(`${API}/dashboard/site-operations`),
        axios.get(`${API}/assets`),
      ]);
      setActions(actionsRes.data);
      setStats(statsRes.data);
      const uniqueSites = [...new Set(assetsRes.data.map((a) => a.site))];
      setSites(uniqueSites);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAction = async () => {
    try {
      await axios.post(`${API}/action-plans`, { ...newAction, source_type: 'manual' });
      setShowCreateDialog(false);
      setNewAction({
        title: '',
        description: '',
        site: '',
        category: 'corrective',
        priority: 'medium',
        risk_level: 'medium',
        owner: '',
        due_date: '',
        sla_hours: 72,
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create action:', error);
    }
  };

  const filteredActions = actions.filter((action) => {
    const matchesSearch =
      searchQuery === '' ||
      action.action_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.site?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || action.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || action.category === categoryFilter;
    const matchesPriority = priorityFilter === 'all' || action.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesCategory && matchesPriority;
  });

  // Group by status for Kanban
  const kanbanColumns = {
    open: filteredActions.filter((a) => a.status === 'open'),
    in_progress: filteredActions.filter((a) => a.status === 'in_progress'),
    completed: filteredActions.filter((a) => a.status === 'completed'),
    verified: filteredActions.filter((a) => a.status === 'verified'),
  };

  const isOverdue = (action) => {
    return action.status !== 'closed' && action.status !== 'verified' && action.due_date < new Date().toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Action Plans</h1>
            <p className="text-slate-500">Centralized operational accountability engine</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list"
              >
                <LayoutList className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('kanban')}
                data-testid="view-kanban"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-action">
                  <Plus className="w-4 h-4 mr-2" />
                  New Action
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                  <DialogTitle>Create Action Plan</DialogTitle>
                  <DialogDescription>Define a new action for operational accountability.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid gap-2">
                    <Label>Title</Label>
                    <Input
                      value={newAction.title}
                      onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                      placeholder="Action title"
                      data-testid="new-action-title"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newAction.description}
                      onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                      placeholder="Describe the action..."
                      data-testid="new-action-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Site</Label>
                      <Select value={newAction.site} onValueChange={(v) => setNewAction({ ...newAction, site: v })}>
                        <SelectTrigger data-testid="new-action-site">
                          <SelectValue placeholder="Select site" />
                        </SelectTrigger>
                        <SelectContent>
                          {sites.map((site) => (
                            <SelectItem key={site} value={site}>{site}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>Category</Label>
                      <Select value={newAction.category} onValueChange={(v) => setNewAction({ ...newAction, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(categoryLabels).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Priority</Label>
                      <Select value={newAction.priority} onValueChange={(v) => setNewAction({ ...newAction, priority: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>Risk Level</Label>
                      <Select value={newAction.risk_level} onValueChange={(v) => setNewAction({ ...newAction, risk_level: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label>Owner</Label>
                    <Input
                      value={newAction.owner}
                      onChange={(e) => setNewAction({ ...newAction, owner: e.target.value })}
                      placeholder="Responsible person"
                      data-testid="new-action-owner"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={newAction.due_date}
                        onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
                        data-testid="new-action-due"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>SLA (hours)</Label>
                      <Select value={newAction.sla_hours.toString()} onValueChange={(v) => setNewAction({ ...newAction, sla_hours: parseInt(v) })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours</SelectItem>
                          <SelectItem value="72">72 hours</SelectItem>
                          <SelectItem value="168">1 week</SelectItem>
                          <SelectItem value="336">2 weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateAction} data-testid="submit-action">Create Action</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border-slate-200" data-testid="stat-open">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Open</span>
                <ClipboardCheck className="w-5 h-5 text-sky-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats?.actions?.open || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-overdue">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Overdue</span>
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-red-600">{stats?.actions?.overdue || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-critical">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Critical</span>
                <Target className="w-5 h-5 text-orange-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-orange-600">{stats?.actions?.critical || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-sla">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">SLA Compliance</span>
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-emerald-600">{stats?.actions?.sla_compliance || 100}%</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-total">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Total Actions</span>
                <CheckCircle className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats?.actions?.total || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search actions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="action-search"
                />
              </div>
              <div className="flex gap-3 flex-wrap">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="priority-filter">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List View */}
        {viewMode === 'list' && (
          <Card className="border-slate-200" data-testid="actions-list">
            <CardContent className="p-0">
              {filteredActions.length === 0 ? (
                <EmptyState
                  icon={TargetIcon}
                  title="No action plans"
                  description={searchQuery ? "Try adjusting your search or filters." : "Actions are created from site visits, alerts, or manually."}
                  actionLabel={!searchQuery ? "Create action" : undefined}
                  onAction={!searchQuery ? () => setShowCreateDialog(true) : undefined}
                />
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredActions.map((action) => (
                    <Link
                      key={action.id}
                      to={`/action-plans/${action.id}`}
                      className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`action-${action.action_number}`}
                    >
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        action.priority === 'critical' ? 'bg-red-100' : action.priority === 'high' ? 'bg-orange-100' : 'bg-slate-100'
                      )}>
                        <ClipboardCheck className={cn(
                          'w-5 h-5',
                          action.priority === 'critical' ? 'text-red-600' : action.priority === 'high' ? 'text-orange-600' : 'text-slate-600'
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-slate-900">{action.action_number}</span>
                          <Badge variant="outline" className={cn('text-xs', priorityColors[action.priority])}>
                            {action.priority.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className={cn('text-xs', categoryColors[action.category])}>
                            {categoryLabels[action.category]}
                          </Badge>
                          <Badge variant="outline" className={cn('text-xs', statusColors[action.status])}>
                            {action.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                          {isOverdue(action) && (
                            <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                              OVERDUE
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium text-slate-900 mt-1 truncate">{action.title}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {action.site}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {action.owner}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Due: {action.due_date}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            Source: {sourceTypeLabels[action.source_type]}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="actions-kanban">
            {Object.entries(kanbanColumns).map(([status, statusActions]) => (
              <div key={status} className="space-y-3">
                <div className="flex items-center justify-between p-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn(statusColors[status])}>
                      {status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <span className="text-sm text-slate-500">{statusActions.length}</span>
                  </div>
                </div>
                <div className="space-y-3 min-h-[200px] p-2 bg-slate-100/50 rounded-lg">
                  {statusActions.map((action) => (
                    <Card
                      key={action.id}
                      className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`kanban-${action.action_number}`}
                    >
                      <Link to={`/action-plans/${action.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-xs font-semibold text-slate-500">{action.action_number}</span>
                            <Badge variant="outline" className={cn('text-xs', priorityColors[action.priority])}>
                              {action.priority.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900 text-sm line-clamp-2">{action.title}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <Badge variant="outline" className={cn('text-xs', categoryColors[action.category])}>
                              {categoryLabels[action.category]}
                            </Badge>
                          </div>
                          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {action.owner?.split(' ')[0]}
                            </span>
                            <span className={cn(isOverdue(action) && 'text-red-600 font-medium')}>
                              {action.due_date}
                            </span>
                          </div>
                        </CardContent>
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
